import React from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Paper,
  Typography,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Input,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  useMediaQuery,
  useTheme,
  Grid,
  Card,
  CardContent,
  Breadcrumbs,
  Link,
  IconButton,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { ArrowBack, Home } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';

// Formata data ISO (yyyy-mm-dd) para dd/mm/yyyy
function formatDateBr(dateStr) {
  if (!dateStr) return '';
  // Se já estiver no formato dd/mm/yyyy, retorna como está
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateStr)) return dateStr;
  const parts = dateStr.split('-');
  if (parts.length === 3) {
    return `${parts[2]}/${parts[1]}/${parts[0]}`;
  }
  return dateStr;
}

function normalizeSearchText(value) {
  return (value || '')
    .toString()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLowerCase();
}

function isAllUppercase(value) {
  return value === value.toUpperCase();
}

function pickPreferredPrestadorLabel(currentValue, candidateValue) {
  if (!currentValue) return candidateValue;
  if (!candidateValue) return currentValue;

  const currentUpper = isAllUppercase(currentValue);
  const candidateUpper = isAllUppercase(candidateValue);

  if (currentUpper && !candidateUpper) return candidateValue;
  if (!currentUpper && candidateUpper) return currentValue;
  if (candidateValue.length > currentValue.length) return candidateValue;
  return currentValue;
}

export default function Contratos() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const { token, hasPermission, isAdmin } = useAuth();
  // const canRead = hasPermission('/contratos', 'read');
  const canCreate = hasPermission('/contratos', 'create');
  const canUpdate = hasPermission('/contratos', 'update');
  // const canDelete = hasPermission('/contratos', 'delete');
  const [rows, setRows] = React.useState([]);
  const [selectedContrato, setSelectedContrato] = React.useState(null);
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  // const [total, setTotal] = React.useState(0);
  const [openModal, setOpenModal] = React.useState(false);
  const [form, setForm] = React.useState({
    numero: '',
    cliente_id: '',
    cliente: '',
    prestador: '',
    valor: '',
    dataInicio: '',
    dataFim: '',
    tipo: '',
    situacao: '',
    prazoPagamento: '',
    quantidadeParcelas: '',
  });
  const [selectedFile, setSelectedFile] = React.useState(null);
  const [editFile, setEditFile] = React.useState(null);
  const [saving, setSaving] = React.useState(false);
  const [editMode, setEditMode] = React.useState(false);
  const [editForm, setEditForm] = React.useState({});
  const [clientes, setClientes] = React.useState([]); // Lista de clientes para o dropdown
  const [prestadoresHistoricos, setPrestadoresHistoricos] = React.useState([]);
  const [openFilter, setOpenFilter] = React.useState(false);
  const [filteredRows, setFilteredRows] = React.useState([]);
  const [filters, setFilters] = React.useState({
    searchText: '',
    clienteId: '',
    prestador: '',
    orderBy: 'numero',
    orderDirection: 'asc',
    valorMin: '',
    valorMax: '',
    dataFimInicio: '',
    dataFimFim: '',
  });
  const API = '/api';
  const handleBack = () => navigate('/');

  const prestadorOptions = React.useMemo(() => {
    const uniquePrestadores = new Map();

    [
      ...prestadoresHistoricos,
      ...rows.map((row) => row.prestadorExibicao || row.prestador || ''),
    ].forEach((rawPrestador) => {
      const prestador = (rawPrestador || '').trim();
      if (!prestador) return;
      const canonicalKey = normalizeSearchText(prestador);
      uniquePrestadores.set(
        canonicalKey,
        pickPreferredPrestadorLabel(uniquePrestadores.get(canonicalKey), prestador)
      );
    });

    return Array.from(uniquePrestadores.values()).sort((a, b) => a.localeCompare(b, 'pt-BR'));
  }, [prestadoresHistoricos, rows]);

  const buildContratoPayload = (raw) => ({
    numero: raw.numero,
    cliente_id: raw.cliente_id,
    cliente_nome: raw.cliente || raw.cliente_nome || '',
    prestador: raw.prestador || '',
    valor: raw.valor,
    data_inicio: raw.dataInicio || raw.data_inicio,
    data_fim: raw.dataFim || raw.data_fim,
    tipo: raw.tipo,
    situacao: raw.situacao,
    prazo_pagamento: raw.prazoPagamento || raw.prazo_pagamento,
    quantidade_parcelas: raw.quantidadeParcelas || raw.quantidade_parcelas,
  });

  const normalizeContrato = (row) => ({
    ...row,
    cliente: row.cliente || row.cliente_nome,
    prestador: row.prestador || '',
    prestadorSugerido: row.prestador_sugerido || row.prestadorSugerido || '',
    prestadorExibicao:
      row.prestador_exibicao ||
      row.prestadorExibicao ||
      row.prestador ||
      row.prestador_sugerido ||
      '',
    dataInicio: row.dataInicio || row.data_inicio,
    dataFim: row.dataFim || row.data_fim,
    prazoPagamento: row.prazoPagamento || row.prazo_pagamento,
    quantidadeParcelas: row.quantidadeParcelas || row.quantidade_parcelas,
  });

  const handleDownloadAnexo = async (contratoId, filename) => {
    try {
      const resp = await fetch(`${API}/contratos/${contratoId}/download`, {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });
      if (!resp.ok) throw new Error('Erro ao baixar arquivo');
      const blob = await resp.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename || `contrato_${contratoId}.bin`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (e) {
      alert('Falha ao baixar arquivo');
    }
  };

  const calcularDiasRestantes = (dataFim, situacao) => {
    if (!dataFim) return null;

    // Se o contrato está concluído, mostra badge verde com data de conclusão
    const sitNorm = (situacao || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');
    if (sitNorm === 'concluido') {
      const dataFormatada = new Date(dataFim).toLocaleDateString('pt-BR');
      return { dias: `Concluído ${dataFormatada}`, cor: '#4caf50', fontWeight: 'bold' };
    }

    const hoje = new Date();
    const dataLimite = new Date(dataFim);
    const diffTime = dataLimite - hoje;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays < 0) {
      return { dias: `${Math.abs(diffDays)}d atraso`, cor: '#f44336', fontWeight: 'bold' };
    } else if (diffDays <= 7) {
      return { dias: `${diffDays}d restam`, cor: '#f44336', fontWeight: 'bold' }; // Vermelho para prazo curto/urgente
    } else if (diffDays <= 30) {
      return { dias: `${diffDays}d restam`, cor: '#ff9800', fontWeight: 'bold' }; // Amarelo para prazo intermediário
    } else {
      return { dias: `${diffDays}d restam`, cor: '#4caf50', fontWeight: 'normal' }; // Verde para prazo longo
    }
  };

  const loadPersisted = async () => {
    try {
      const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};

      // Verificar quais clientes o usuário tem acesso
      let clienteIds = null;
      try {
        const meResponse = await fetch(`${API}/me/clientes`, { headers: authHeaders });
        if (meResponse.ok) {
          const meData = await meResponse.json();
          clienteIds = meData.clientes || [];
        }
      } catch (e) {
        console.warn('Endpoint /me/clientes não disponível');
      }

      const response = await fetch(`${API}/contratos`, {
        headers: authHeaders,
      });
      if (response.ok) {
        const data = await response.json();
        let normalized = Array.isArray(data) ? data.map(normalizeContrato) : [];
        // Filtrar contratos por clientes permitidos
        if (clienteIds && clienteIds.length > 0) {
          const idsNum = clienteIds.map(Number);
          normalized = normalized.filter((c) => idsNum.includes(Number(c.cliente_id)));
        }
        setRows(normalized);
        setFilteredRows(normalized);
      } else if (response.status === 401 || response.status === 403) {
        setRows([]);
        setFilteredRows([]);
      }
    } catch (error) {
      console.error('Erro ao carregar contratos:', error);
    }
  };

  const loadClientes = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      // Verificar quais clientes o usuário tem acesso
      let clienteIds = null;
      try {
        const meResponse = await fetch(`${API}/me/clientes`, { headers });
        if (meResponse.ok) {
          const meData = await meResponse.json();
          clienteIds = meData.clientes || [];
        }
      } catch (e) {
        console.warn('Endpoint /me/clientes não disponível');
      }

      const response = await fetch(`${API}/clientes/`, { headers });
      if (response.ok) {
        let data = await response.json();
        // Filtrar se houver restrição
        if (clienteIds && clienteIds.length > 0) {
          data = data.filter((c) => clienteIds.map(Number).includes(Number(c.id)));
        }
        setClientes(data);
      }
    } catch (error) {
      console.error('Erro ao carregar clientes:', error);
    }
  };

  const loadPrestadoresHistoricos = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await fetch(`${API}/contratos/prestadores`, { headers });
      if (response.ok) {
        const data = await response.json();
        setPrestadoresHistoricos(Array.isArray(data) ? data : []);
      }
    } catch (error) {
      console.error('Erro ao carregar prestadores para autocomplete:', error);
    }
  };

  const applyFilters = React.useCallback(() => {
    let filtered = [...rows];

    if (filters.searchText) {
      filtered = filtered.filter(
        (row) =>
          (row.numero || '').toString().toLowerCase().includes(filters.searchText.toLowerCase()) ||
          (row.cliente || '').toLowerCase().includes(filters.searchText.toLowerCase()) ||
          (row.prestadorExibicao || '').toLowerCase().includes(filters.searchText.toLowerCase())
      );
    }

    if (filters.prestador) {
      filtered = filtered.filter((row) =>
        (row.prestadorExibicao || '').toLowerCase().includes(filters.prestador.toLowerCase())
      );
    }

    if (filters.clienteId) {
      filtered = filtered.filter(
        (row) => String(row.cliente_id || '') === String(filters.clienteId)
      );
    }

    if (filters.valorMin) {
      filtered = filtered.filter(
        (row) => parseFloat(row.valor || 0) >= parseFloat(filters.valorMin)
      );
    }

    if (filters.valorMax) {
      filtered = filtered.filter(
        (row) => parseFloat(row.valor || 0) <= parseFloat(filters.valorMax)
      );
    }

    if (filters.dataFimInicio) {
      filtered = filtered.filter((row) => row.dataFim >= filters.dataFimInicio);
    }

    if (filters.dataFimFim) {
      filtered = filtered.filter((row) => row.dataFim <= filters.dataFimFim);
    }

    filtered.sort((a, b) => {
      let aVal = a[filters.orderBy] || '';
      let bVal = b[filters.orderBy] || '';

      if (filters.orderBy === 'valor') {
        aVal = parseFloat(aVal) || 0;
        bVal = parseFloat(bVal) || 0;
      } else if (filters.orderBy === 'dataFim') {
        aVal = new Date(aVal);
        bVal = new Date(bVal);
      }

      if (filters.orderDirection === 'asc') {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });

    setFilteredRows(filtered);
    setPage(0);
  }, [filters, rows]);

  React.useEffect(() => {
    applyFilters();
  }, [applyFilters]);

  const loadPersistedCb = React.useCallback(loadPersisted, [API, token]);
  const loadClientesCb = React.useCallback(loadClientes, [API, token]);
  const loadPrestadoresHistoricosCb = React.useCallback(loadPrestadoresHistoricos, [API, token]);
  React.useEffect(() => {
    loadPersistedCb();
    loadClientesCb();
    loadPrestadoresHistoricosCb();
  }, [loadPersistedCb, loadClientesCb, loadPrestadoresHistoricosCb]);

  return (
    <Box
      sx={{
        background: theme.palette.background.default,
        minHeight: '100vh',
        p: { xs: 1, sm: 2, md: 4 },
      }}
    >
      <Box sx={{ maxWidth: 1600, mx: 'auto', mt: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <IconButton onClick={handleBack} size="small" aria-label="Voltar ao Dashboard">
            <ArrowBack />
          </IconButton>
          <Breadcrumbs separator="/" sx={{ ml: 1, '& .MuiBreadcrumbs-separator': { mx: 0.5 } }}>
            <Link
              underline="hover"
              color="inherit"
              onClick={handleBack}
              sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
            >
              <Home sx={{ mr: 0.5 }} fontSize="inherit" />
              Dashboard
            </Link>
            <Typography color="text.primary">Contratos</Typography>
          </Breadcrumbs>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              mb: 0,
              fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' },
            }}
          >
            Gestão de Contratos
          </Typography>
        </Box>

        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Gerencie seus contratos de forma eficiente
        </Typography>

        {/* Header com botões responsivos */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 2,
            mb: 3,
            alignItems: { xs: 'stretch', sm: 'center' },
          }}
        >
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ flex: 1 }}>
            {canCreate && (
              <Button
                variant="contained"
                onClick={() => {
                  setForm({
                    numero: '',
                    cliente_id: '',
                    cliente: '',
                    prestador: '',
                    valor: '',
                    dataInicio: '',
                    dataFim: '',
                    tipo: '',
                    situacao: '',
                    prazoPagamento: '',
                    quantidadeParcelas: '',
                  });
                  setSelectedFile(null);
                  setOpenModal(true);
                }}
                size={isMobile ? 'small' : 'medium'}
                sx={{ fontWeight: 600 }}
              >
                Novo Contrato
              </Button>
            )}
            <Button
              variant="outlined"
              onClick={() => setOpenFilter(true)}
              size={isMobile ? 'small' : 'medium'}
            >
              Filtros
            </Button>
          </Stack>
        </Box>

        {/* Chips de filtro */}
        <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
          {filters.searchText && (
            <Chip
              label={`Busca: ${filters.searchText}`}
              onDelete={() => setFilters((f) => ({ ...f, searchText: '' }))}
              size={isMobile ? 'small' : 'medium'}
            />
          )}
          {filters.prestador && (
            <Chip
              label={`Prestador: ${filters.prestador}`}
              onDelete={() => setFilters((f) => ({ ...f, prestador: '' }))}
              size={isMobile ? 'small' : 'medium'}
            />
          )}
          {filters.clienteId && (
            <Chip
              label={`Cliente: ${clientes.find((c) => String(c.id) === String(filters.clienteId))?.nome || filters.clienteId}`}
              onDelete={() => setFilters((f) => ({ ...f, clienteId: '' }))}
              size={isMobile ? 'small' : 'medium'}
            />
          )}
          {filters.orderBy !== 'numero' && (
            <Chip
              label={`Ordem: ${filters.orderBy} ${filters.orderDirection}`}
              onDelete={() =>
                setFilters((f) => ({ ...f, orderBy: 'numero', orderDirection: 'asc' }))
              }
              size={isMobile ? 'small' : 'medium'}
            />
          )}
          {(filters.valorMin || filters.valorMax) && (
            <Chip
              label="Filtro Valor"
              onDelete={() => setFilters((f) => ({ ...f, valorMin: '', valorMax: '' }))}
              size={isMobile ? 'small' : 'medium'}
            />
          )}
        </Stack>

        {/* Versão Desktop - Tabela */}
        {!isMobile ? (
          <TableContainer
            component={Paper}
            sx={{ borderRadius: 2, boxShadow: 0, overflowX: 'hidden' }}
          >
            <Table size="small" sx={{ tableLayout: 'fixed', width: '100%' }}>
              <TableHead>
                <TableRow sx={{ background: '#f4f6f8' }}>
                  <TableCell sx={{ fontWeight: 700, width: '7%', whiteSpace: 'nowrap' }}>
                    O.S
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, width: '16%' }}>Cliente</TableCell>
                  <TableCell sx={{ fontWeight: 700, width: '16%' }}>Prestador</TableCell>
                  <TableCell sx={{ fontWeight: 700, width: '10%', whiteSpace: 'nowrap' }}>
                    Valor
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, width: '9%', whiteSpace: 'nowrap' }}>
                    Data Início
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, width: '15%', whiteSpace: 'nowrap' }}>
                    Data Fim
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, width: '9%' }}>Tipo</TableCell>
                  <TableCell sx={{ fontWeight: 700, width: '8%' }}>Situação</TableCell>
                  <TableCell sx={{ fontWeight: 700, width: '10%', whiteSpace: 'nowrap' }}>
                    Ações
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredRows
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((row, idx) => {
                    const diasRestantes = calcularDiasRestantes(row.dataFim, row.situacao);
                    const isCancelado = (row.situacao || '').toLowerCase() === 'cancelado';
                    return (
                      <TableRow
                        key={row.id || idx}
                        hover
                        sx={{
                          borderBottom: '1px solid #e0e0e0',
                          backgroundColor: isCancelado ? '#fde2e4' : 'transparent',
                          '& > td:first-of-type': {
                            borderLeft: isCancelado ? '6px solid #f48fb1' : 'none',
                          },
                        }}
                      >
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{row.numero || ''}</TableCell>
                        <TableCell
                          sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {row.cliente || ''}
                        </TableCell>
                        <TableCell
                          sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {row.prestadorExibicao || '-'}
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          {row.valor
                            ? `R$ ${parseFloat(row.valor).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                            : ''}
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          {formatDateBr(row.dataInicio)}
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <span>{formatDateBr(row.dataFim)}</span>
                            {diasRestantes && (
                              <Chip
                                label={diasRestantes.dias}
                                size="small"
                                sx={{
                                  backgroundColor: diasRestantes.cor,
                                  color: 'white',
                                  fontWeight: diasRestantes.fontWeight,
                                  fontSize: '0.75rem',
                                }}
                              />
                            )}
                          </Box>
                        </TableCell>
                        <TableCell
                          sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {row.tipo || ''}
                        </TableCell>
                        <TableCell
                          sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {row.situacao || ''}
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          <Button
                            size="small"
                            variant="text"
                            onClick={() => setSelectedContrato(row)}
                          >
                            Detalhes
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={filteredRows.length}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[5, 10, 25]}
              labelRowsPerPage="Linhas por página:"
            />
          </TableContainer>
        ) : (
          /* Versão Mobile - Cards */
          <Box>
            {filteredRows
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((row, idx) => {
                const diasRestantes = calcularDiasRestantes(row.dataFim, row.situacao);
                const isCancelado = (row.situacao || '').toLowerCase() === 'cancelado';
                return (
                  <Card
                    key={row.id || idx}
                    sx={{
                      mb: 2,
                      borderRadius: 2,
                      backgroundColor: isCancelado ? '#fde2e4' : 'background.paper',
                      borderLeft: isCancelado ? '6px solid #f48fb1' : 'none',
                    }}
                  >
                    <CardContent>
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'flex-start',
                          mb: 2,
                        }}
                      >
                        <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
                          O.S #{row.numero || ''}
                        </Typography>
                        <Chip
                          label={row.situacao || ''}
                          size="small"
                          variant="outlined"
                          sx={{ backgroundColor: '#f5f5f5' }}
                        />
                      </Box>

                      <Typography variant="body1" sx={{ mb: 1, fontWeight: 500 }}>
                        {row.cliente || ''}
                      </Typography>

                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                        Prestador: {row.prestadorExibicao || '-'}
                      </Typography>

                      <Typography variant="h6" color="primary" sx={{ mb: 2, fontWeight: 600 }}>
                        {row.valor
                          ? `R$ ${parseFloat(row.valor).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                          : ''}
                      </Typography>

                      <Grid container spacing={1} sx={{ mb: 2 }}>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary">
                            Início
                          </Typography>
                          <Typography variant="body2">{formatDateBr(row.dataInicio)}</Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary">
                            Fim
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body2">{formatDateBr(row.dataFim)}</Typography>
                            {diasRestantes && (
                              <Chip
                                label={diasRestantes.dias}
                                size="small"
                                sx={{
                                  backgroundColor: diasRestantes.cor,
                                  color: 'white',
                                  fontWeight: diasRestantes.fontWeight,
                                  fontSize: '0.7rem',
                                  height: 20,
                                }}
                              />
                            )}
                          </Box>
                        </Grid>
                        <Grid item xs={12}>
                          <Typography variant="caption" color="text.secondary">
                            Tipo
                          </Typography>
                          <Typography variant="body2">{row.tipo || ''}</Typography>
                        </Grid>
                      </Grid>

                      <Button
                        size="small"
                        variant="contained"
                        onClick={() => setSelectedContrato(row)}
                        fullWidth
                      >
                        Ver detalhes
                      </Button>
                    </CardContent>
                  </Card>
                );
              })}

            {/* Paginação Mobile */}
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <TablePagination
                component="div"
                count={filteredRows.length}
                page={page}
                onPageChange={(_, newPage) => setPage(newPage)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
                rowsPerPageOptions={[5, 10, 25]}
                labelRowsPerPage="Por página:"
                sx={{
                  '& .MuiTablePagination-toolbar': {
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 1,
                  },
                  '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                    fontSize: '0.875rem',
                  },
                }}
              />
            </Box>
          </Box>
        )}

        {/* Modal de Filtros */}
        <Dialog open={openFilter} onClose={() => setOpenFilter(false)} maxWidth="md" fullWidth>
          <DialogTitle>Filtros e Ordenação</DialogTitle>
          <DialogContent sx={{ minWidth: isMobile ? 300 : 500 }}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
                gap: 2,
                mt: 1,
              }}
            >
              {/* Busca por texto */}
              <TextField
                label="Buscar por O.S, Cliente ou Prestador"
                fullWidth
                value={filters.searchText}
                onChange={(e) => setFilters((f) => ({ ...f, searchText: e.target.value }))}
                placeholder="Digite para buscar..."
              />

              <FormControl fullWidth>
                <InputLabel>Filtrar por Cliente</InputLabel>
                <Select
                  value={filters.clienteId}
                  onChange={(e) => setFilters((f) => ({ ...f, clienteId: e.target.value }))}
                  label="Filtrar por Cliente"
                >
                  <MenuItem value="">Todos os clientes</MenuItem>
                  {clientes.map((cliente) => (
                    <MenuItem key={cliente.id} value={cliente.id}>
                      ID: {cliente.id} - {cliente.nome}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                label="Filtrar por Prestador"
                fullWidth
                value={filters.prestador}
                onChange={(e) => setFilters((f) => ({ ...f, prestador: e.target.value }))}
                placeholder="Nome do prestador"
              />

              {/* Ordenação */}
              <FormControl fullWidth>
                <InputLabel>Ordenar por</InputLabel>
                <Select
                  value={filters.orderBy}
                  onChange={(e) => setFilters((f) => ({ ...f, orderBy: e.target.value }))}
                  label="Ordenar por"
                >
                  <MenuItem value="numero">O.S</MenuItem>
                  <MenuItem value="cliente">Cliente (A-Z)</MenuItem>
                  <MenuItem value="prestadorExibicao">Prestador (A-Z)</MenuItem>
                  <MenuItem value="valor">Valor</MenuItem>
                  <MenuItem value="dataFim">Data Fim</MenuItem>
                </Select>
              </FormControl>

              {/* Direção da ordenação */}
              <FormControl fullWidth>
                <InputLabel>Direção</InputLabel>
                <Select
                  value={filters.orderDirection}
                  onChange={(e) => setFilters((f) => ({ ...f, orderDirection: e.target.value }))}
                  label="Direção"
                >
                  <MenuItem value="asc">Crescente (A-Z, Menor-Maior, Mais próximo)</MenuItem>
                  <MenuItem value="desc">Decrescente (Z-A, Maior-Menor, Mais distante)</MenuItem>
                </Select>
              </FormControl>

              {/* Valor mínimo */}
              <TextField
                label="Valor Mínimo (R$)"
                type="number"
                fullWidth
                value={filters.valorMin}
                onChange={(e) => setFilters((f) => ({ ...f, valorMin: e.target.value }))}
                placeholder="0.00"
              />

              {/* Valor máximo */}
              <TextField
                label="Valor Máximo (R$)"
                type="number"
                fullWidth
                value={filters.valorMax}
                onChange={(e) => setFilters((f) => ({ ...f, valorMax: e.target.value }))}
                placeholder="0.00"
              />

              {/* Data fim início */}
              <TextField
                label="Data Fim - A partir de"
                type="date"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={filters.dataFimInicio}
                onChange={(e) => setFilters((f) => ({ ...f, dataFimInicio: e.target.value }))}
              />

              {/* Data fim final */}
              <TextField
                label="Data Fim - Até"
                type="date"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={filters.dataFimFim}
                onChange={(e) => setFilters((f) => ({ ...f, dataFimFim: e.target.value }))}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setFilters({
                  searchText: '',
                  clienteId: '',
                  prestador: '',
                  orderBy: 'numero',
                  orderDirection: 'asc',
                  valorMin: '',
                  valorMax: '',
                  dataFimInicio: '',
                  dataFimFim: '',
                });
              }}
            >
              Limpar Filtros
            </Button>
            <Button onClick={() => setOpenFilter(false)}>Fechar</Button>
          </DialogActions>
        </Dialog>

        {/* Modal de cadastro */}
        <Dialog
          open={openModal}
          onClose={() => {
            setOpenModal(false);
            setForm({
              numero: '',
              cliente_id: '',
              cliente: '',
              valor: '',
              dataInicio: '',
              dataFim: '',
              tipo: '',
              situacao: '',
              prazoPagamento: '',
              quantidadeParcelas: '',
            });
            setSelectedFile(null);
          }}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Novo Contrato</DialogTitle>
          <DialogContent>
            <TextField
              margin="normal"
              label="Número O.S"
              fullWidth
              value={form.numero}
              onChange={(e) => setForm((f) => ({ ...f, numero: e.target.value }))}
            />

            <FormControl fullWidth margin="normal">
              <InputLabel>Cliente</InputLabel>
              <Select
                value={form.cliente_id}
                label="Cliente"
                onChange={(e) => {
                  const clienteId = e.target.value;
                  const cliente = clientes.find((c) => c.id === clienteId);
                  setForm((f) => ({
                    ...f,
                    cliente_id: clienteId,
                    cliente: cliente ? cliente.nome : '',
                  }));
                }}
              >
                {clientes.map((cliente) => (
                  <MenuItem key={cliente.id} value={cliente.id}>
                    ID: {cliente.id} - {cliente.nome}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Autocomplete
              freeSolo
              options={prestadorOptions}
              inputValue={form.prestador || ''}
              filterOptions={(options, state) => {
                const input = normalizeSearchText(state.inputValue);
                if (!input) return options;
                return options.filter((option) => normalizeSearchText(option).includes(input));
              }}
              onInputChange={(_, newInputValue) =>
                setForm((f) => ({ ...f, prestador: newInputValue }))
              }
              onChange={(_, newValue) =>
                setForm((f) => ({
                  ...f,
                  prestador: typeof newValue === 'string' ? newValue : newValue || '',
                }))
              }
              renderInput={(params) => (
                <TextField {...params} margin="normal" label="Prestador" fullWidth />
              )}
            />

            <TextField
              margin="normal"
              label="Valor (R$)"
              type="number"
              fullWidth
              value={form.valor}
              onChange={(e) => setForm((f) => ({ ...f, valor: e.target.value }))}
            />
            <TextField
              margin="normal"
              label="Data Início"
              type="date"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={form.dataInicio}
              onChange={(e) => setForm((f) => ({ ...f, dataInicio: e.target.value }))}
            />
            <TextField
              margin="normal"
              label="Data Fim"
              type="date"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={form.dataFim}
              onChange={(e) => setForm((f) => ({ ...f, dataFim: e.target.value }))}
            />

            <FormControl fullWidth margin="normal">
              <InputLabel>Tipo</InputLabel>
              <Select
                value={form.tipo}
                onChange={(e) => setForm((f) => ({ ...f, tipo: e.target.value }))}
                label="Tipo"
              >
                <MenuItem value="Obra">Obra</MenuItem>
                <MenuItem value="Projetos">Projetos</MenuItem>
                <MenuItem value="Reforma">Reforma</MenuItem>
                <MenuItem value="Manutenção">Manutenção</MenuItem>
                <MenuItem value="Serviços">Serviços</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth margin="normal">
              <InputLabel>Situação</InputLabel>
              <Select
                value={form.situacao}
                onChange={(e) => setForm((f) => ({ ...f, situacao: e.target.value }))}
                label="Situação"
              >
                <MenuItem value="Em andamento">Em andamento</MenuItem>
                <MenuItem value="Concluído">Concluído</MenuItem>
                <MenuItem value="Cancelado">Cancelado</MenuItem>
                <MenuItem value="Pausado">Pausado</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth margin="normal">
              <InputLabel>Prazo de Pagamento</InputLabel>
              <Select
                value={form.prazoPagamento}
                onChange={(e) => setForm((f) => ({ ...f, prazoPagamento: e.target.value }))}
                label="Prazo de Pagamento"
              >
                <MenuItem value="À vista">À vista</MenuItem>
                {Array.from({ length: 180 }, (_, i) => i + 1).map((dias) => (
                  <MenuItem key={dias} value={`${dias} DD`}>
                    {dias} DD
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth margin="normal">
              <InputLabel>Quantidade de Parcelas</InputLabel>
              <Select
                value={form.quantidadeParcelas}
                onChange={(e) => setForm((f) => ({ ...f, quantidadeParcelas: e.target.value }))}
                label="Quantidade de Parcelas"
              >
                <MenuItem value="1">1x</MenuItem>
                {Array.from({ length: 36 }, (_, i) => i + 2).map((parcelas) => (
                  <MenuItem key={parcelas} value={parcelas}>
                    {parcelas}x
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Input
              type="file"
              inputProps={{ accept: 'image/*,application/pdf' }}
              onChange={(e) => setSelectedFile(e.target.files[0])}
              sx={{ mt: 2 }}
              fullWidth
            />
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setOpenModal(false);
                setForm({
                  numero: '',
                  cliente_id: '',
                  cliente: '',
                  prestador: '',
                  valor: '',
                  dataInicio: '',
                  dataFim: '',
                  tipo: '',
                  situacao: '',
                  prazoPagamento: '',
                  quantidadeParcelas: '',
                });
                setSelectedFile(null);
              }}
            >
              Cancelar
            </Button>
            <Button
              variant="contained"
              onClick={async () => {
                if (!form.cliente_id) {
                  alert('Por favor, selecione um cliente');
                  return;
                }

                setSaving(true);
                try {
                  const formData = new FormData();
                  const payload = buildContratoPayload(form);
                  Object.entries(payload).forEach(([key, value]) => {
                    if (value !== undefined && value !== null && value !== '') {
                      formData.append(`contrato[${key}]`, value);
                    }
                  });
                  if (selectedFile) formData.append('contrato[arquivo]', selectedFile);

                  const response = await fetch(`${API}/contratos`, {
                    method: 'POST',
                    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
                    body: formData,
                  });

                  if (response.ok) {
                    await loadPersisted();
                    setForm({
                      numero: '',
                      cliente_id: '',
                      cliente: '',
                      valor: '',
                      dataInicio: '',
                      dataFim: '',
                      tipo: '',
                      situacao: '',
                      prazoPagamento: '',
                      quantidadeParcelas: '',
                    });
                    setSelectedFile(null);
                    setOpenModal(false);
                  } else {
                    alert('Erro ao criar contrato');
                  }
                } catch (e) {
                  alert('Erro ao criar contrato');
                } finally {
                  setSaving(false);
                }
              }}
              disabled={saving || !canCreate}
            >
              {saving ? 'Salvando...' : 'Criar contrato'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Modal de detalhes do contrato */}
        <Dialog
          open={!!selectedContrato}
          onClose={() => {
            setSelectedContrato(null);
            setEditMode(false);
            setEditForm({});
            setEditFile(null);
          }}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>{editMode ? 'Editar Contrato' : 'Detalhes do Contrato'}</DialogTitle>
          <DialogContent>
            {selectedContrato && (
              <Box sx={{ mt: 1 }}>
                {!editMode ? (
                  <>
                    <Typography>
                      <b>O.S:</b> {selectedContrato.numero}
                    </Typography>
                    <Typography>
                      <b>Cliente:</b> {selectedContrato.cliente}
                    </Typography>
                    <Typography>
                      <b>Prestador:</b> {selectedContrato.prestadorExibicao || '-'}
                    </Typography>
                    <Typography>
                      <b>Valor:</b> R${' '}
                      {parseFloat(selectedContrato.valor || 0).toLocaleString('pt-BR', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </Typography>
                    <Typography>
                      <b>Data Início:</b> {formatDateBr(selectedContrato.dataInicio)}
                    </Typography>
                    <Typography>
                      <b>Data Fim:</b> {formatDateBr(selectedContrato.dataFim)}
                    </Typography>
                    <Typography>
                      <b>Tipo:</b> {selectedContrato.tipo}
                    </Typography>
                    <Typography>
                      <b>Situação:</b> {selectedContrato.situacao}
                    </Typography>
                    <Typography>
                      <b>Prazo Pagamento:</b> {selectedContrato.prazoPagamento}
                    </Typography>
                    <Typography>
                      <b>Parcelas:</b> {selectedContrato.quantidadeParcelas}
                    </Typography>

                    {selectedContrato.arquivo && (
                      <Box sx={{ mt: 2 }}>
                        <Typography>
                          <b>Anexo:</b>
                        </Typography>
                        <Button
                          variant="outlined"
                          sx={{ mt: 1 }}
                          onClick={() =>
                            handleDownloadAnexo(selectedContrato.id, selectedContrato.arquivo)
                          }
                        >
                          Baixar arquivo ({selectedContrato.arquivo})
                        </Button>
                      </Box>
                    )}
                  </>
                ) : (
                  <>
                    <TextField
                      margin="normal"
                      label="Número O.S"
                      fullWidth
                      value={editForm.numero || ''}
                      onChange={(e) => setEditForm((f) => ({ ...f, numero: e.target.value }))}
                    />

                    <FormControl fullWidth margin="normal">
                      <InputLabel>Cliente</InputLabel>
                      <Select
                        value={editForm.cliente_id || ''}
                        label="Cliente"
                        onChange={(e) => {
                          const clienteId = e.target.value;
                          const cliente = clientes.find((c) => c.id === clienteId);
                          setEditForm((f) => ({
                            ...f,
                            cliente_id: clienteId,
                            cliente: cliente ? cliente.nome : '',
                          }));
                        }}
                      >
                        {clientes.map((cliente) => (
                          <MenuItem key={cliente.id} value={cliente.id}>
                            ID: {cliente.id} - {cliente.nome}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    <Autocomplete
                      freeSolo
                      options={prestadorOptions}
                      inputValue={editForm.prestador || ''}
                      filterOptions={(options, state) => {
                        const input = normalizeSearchText(state.inputValue);
                        if (!input) return options;
                        return options.filter((option) =>
                          normalizeSearchText(option).includes(input)
                        );
                      }}
                      onInputChange={(_, newInputValue) =>
                        setEditForm((f) => ({ ...f, prestador: newInputValue }))
                      }
                      onChange={(_, newValue) =>
                        setEditForm((f) => ({
                          ...f,
                          prestador: typeof newValue === 'string' ? newValue : newValue || '',
                        }))
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          margin="normal"
                          label="Prestador"
                          fullWidth
                          helperText={
                            !editForm.prestador && selectedContrato.prestadorSugerido
                              ? `Sugestão pelo anexo: ${selectedContrato.prestadorSugerido}`
                              : 'Preencha para contratos antigos ou ajuste quando necessário.'
                          }
                        />
                      )}
                    />

                    <TextField
                      margin="normal"
                      label="Valor (R$)"
                      type="number"
                      fullWidth
                      value={editForm.valor || ''}
                      onChange={(e) => setEditForm((f) => ({ ...f, valor: e.target.value }))}
                    />
                    <TextField
                      margin="normal"
                      label="Data Início"
                      type="date"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      value={editForm.dataInicio || ''}
                      onChange={(e) => setEditForm((f) => ({ ...f, dataInicio: e.target.value }))}
                    />
                    <TextField
                      margin="normal"
                      label="Data Fim"
                      type="date"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      value={editForm.dataFim || ''}
                      onChange={(e) => setEditForm((f) => ({ ...f, dataFim: e.target.value }))}
                    />

                    <FormControl fullWidth margin="normal">
                      <InputLabel>Tipo</InputLabel>
                      <Select
                        value={editForm.tipo || ''}
                        onChange={(e) => setEditForm((f) => ({ ...f, tipo: e.target.value }))}
                        label="Tipo"
                      >
                        <MenuItem value="Obra">Obra</MenuItem>
                        <MenuItem value="Projetos">Projetos</MenuItem>
                        <MenuItem value="Reforma">Reforma</MenuItem>
                        <MenuItem value="Manutenção">Manutenção</MenuItem>
                        <MenuItem value="Serviços">Serviços</MenuItem>
                      </Select>
                    </FormControl>

                    <FormControl fullWidth margin="normal">
                      <InputLabel>Situação</InputLabel>
                      <Select
                        value={editForm.situacao || ''}
                        onChange={(e) => setEditForm((f) => ({ ...f, situacao: e.target.value }))}
                        label="Situação"
                      >
                        <MenuItem value="Em andamento">Em andamento</MenuItem>
                        <MenuItem value="Concluído">Concluído</MenuItem>
                        <MenuItem value="Cancelado">Cancelado</MenuItem>
                        <MenuItem value="Pausado">Pausado</MenuItem>
                      </Select>
                    </FormControl>

                    {selectedContrato.arquivo && (
                      <Box sx={{ mt: 2 }}>
                        <Typography>
                          <b>Anexo atual:</b>
                        </Typography>
                        <Button
                          variant="outlined"
                          sx={{ mt: 1 }}
                          onClick={() =>
                            handleDownloadAnexo(selectedContrato.id, selectedContrato.arquivo)
                          }
                        >
                          Baixar arquivo ({selectedContrato.arquivo})
                        </Button>
                      </Box>
                    )}

                    <Input
                      type="file"
                      inputProps={{ accept: 'image/*,application/pdf' }}
                      onChange={(e) => setEditFile(e.target.files[0])}
                      sx={{ mt: 2 }}
                      fullWidth
                    />
                  </>
                )}
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            {!editMode ? (
              <>
                {canUpdate && (
                  <Button
                    onClick={() => {
                      setEditMode(true);
                      setEditForm({
                        numero: selectedContrato.numero,
                        cliente_id: selectedContrato.cliente_id,
                        cliente: selectedContrato.cliente,
                        prestador:
                          selectedContrato.prestador || selectedContrato.prestadorSugerido || '',
                        valor: selectedContrato.valor,
                        dataInicio: selectedContrato.dataInicio,
                        dataFim: selectedContrato.dataFim,
                        tipo: selectedContrato.tipo,
                        situacao: selectedContrato.situacao,
                        prazoPagamento: selectedContrato.prazoPagamento,
                        quantidadeParcelas: selectedContrato.quantidadeParcelas,
                      });
                    }}
                  >
                    Editar
                  </Button>
                )}
                {isAdmin && (
                  <Button
                    color="error"
                    onClick={async () => {
                      if (!window.confirm('Deseja realmente excluir este contrato?')) return;
                      setSaving(true);
                      try {
                        const response = await fetch(`${API}/contratos/${selectedContrato.id}`, {
                          method: 'DELETE',
                          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
                        });

                        if (response.ok || response.status === 204) {
                          await loadPersisted();
                          setSelectedContrato(null);
                          setEditMode(false);
                          setEditForm({});
                          setEditFile(null);
                        } else {
                          alert('Erro ao excluir contrato');
                        }
                      } catch (e) {
                        alert('Erro ao excluir contrato');
                      } finally {
                        setSaving(false);
                      }
                    }}
                    disabled={saving}
                  >
                    Excluir
                  </Button>
                )}
                <Button onClick={() => setSelectedContrato(null)}>Fechar</Button>
              </>
            ) : (
              <>
                <Button
                  onClick={() => {
                    setEditMode(false);
                    setEditForm({});
                    setEditFile(null);
                  }}
                >
                  Cancelar
                </Button>
                <Button
                  variant="contained"
                  onClick={async () => {
                    setSaving(true);
                    try {
                      const payload = buildContratoPayload(editForm);
                      let response;

                      if (editFile) {
                        const formData = new FormData();
                        Object.entries(payload).forEach(([key, value]) => {
                          if (value !== undefined && value !== null && value !== '') {
                            formData.append(`contrato[${key}]`, value);
                          }
                        });
                        formData.append('contrato[arquivo]', editFile);

                        response = await fetch(`${API}/contratos/${selectedContrato.id}`, {
                          method: 'PUT',
                          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
                          body: formData,
                        });
                      } else {
                        response = await fetch(`${API}/contratos/${selectedContrato.id}`, {
                          method: 'PUT',
                          headers: {
                            'Content-Type': 'application/json',
                            ...(token ? { Authorization: `Bearer ${token}` } : {}),
                          },
                          body: JSON.stringify({ contrato: payload }),
                        });
                      }

                      if (response.ok) {
                        await loadPersisted();
                        setSelectedContrato(null);
                        setEditMode(false);
                        setEditForm({});
                        setEditFile(null);
                      } else {
                        alert('Erro ao atualizar contrato');
                      }
                    } catch (e) {
                      alert('Erro ao atualizar contrato');
                    } finally {
                      setSaving(false);
                    }
                  }}
                  disabled={saving || !canUpdate}
                >
                  {saving ? 'Salvando...' : 'Salvar'}
                </Button>
              </>
            )}
          </DialogActions>
        </Dialog>

        {/* Status */}
        <Typography variant="body2" color="text.secondary" sx={{ mt: 4, textAlign: 'center' }}>
          {filteredRows.length === 0
            ? 'Nenhum contrato encontrado'
            : `${filteredRows.length} contratos encontrados`}
        </Typography>
      </Box>
    </Box>
  );
}
